import React, { useRef, useState } from 'react';
import {
  IconTool,
  IconSearch,
  IconClipboardCheck,
  IconFileInvoice,
  IconCreditCard,
  IconReceipt,
  IconTruckDelivery,
  IconUsers,
  IconBox,
  IconMenu2,
  type Icon,
} from '@tabler/icons-react';
import { ActionPicker, type ActionPickerOption } from './dropdown';
import { usePartsToOrderCount } from '../hooks/usePartsToOrderCount';
import styles from './AppNav.module.css';

interface NavItem {
  key: string;
  label: string;
  Icon: Icon;
  /** Stays a tab on narrow screens; the rest fold into the More menu. */
  primary?: boolean;
}

/* The everyday working surfaces. Calls, Calendar, Print and Admin are
   deliberately absent — they are utilities and live in the Header's topbar
   cluster, which is what keeps this row inside a 1280px viewport.

   Below the collapse breakpoint (AppNav.module.css) only the `primary` four stay
   as tabs and the rest move into a More menu, so the strip never scrolls sideways. */
const NAV_ITEMS: NavItem[] = [
  { key: 'work-items', label: 'Work items', Icon: IconTool, primary: true },
  { key: 'search', label: 'Search', Icon: IconSearch, primary: true },
  { key: 'check-ins', label: 'Check-ins', Icon: IconClipboardCheck, primary: true },
  { key: 'invoices', label: 'Invoices', Icon: IconFileInvoice, primary: true },
  { key: 'payments', label: 'Payments', Icon: IconCreditCard },
  { key: 'quotes', label: 'Quotes', Icon: IconReceipt },
  { key: 'part-orders', label: 'Part orders', Icon: IconTruckDelivery },
  { key: 'customers', label: 'Customers', Icon: IconUsers },
  { key: 'inventory', label: 'Inventory', Icon: IconBox },
];

const SECONDARY_KEYS = new Set(NAV_ITEMS.filter((i) => !i.primary).map((i) => i.key));

interface AppNavProps {
  activeRoute: string;
  onNavChange: (route: string) => void;
}

export const AppNav: React.FC<AppNavProps> = ({ activeRoute, onNavChange }) => {
  const partsToOrderCount = usePartsToOrderCount();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);

  // When the current page is one of the folded items, More is what reads as
  // current — otherwise nothing in the strip would say where you are.
  const moreActive = SECONDARY_KEYS.has(activeRoute);

  // ActionPicker rows take a plain label, so the parts-to-order count rides in it;
  // the badge on More itself is what keeps the count visible while folded away.
  const moreOptions: ActionPickerOption[] = NAV_ITEMS.filter((i) => !i.primary).map(
    ({ key, label, Icon }) => ({
      value: key,
      label: key === 'part-orders' && partsToOrderCount > 0 ? `${label} (${partsToOrderCount})` : label,
      Icon,
    })
  );

  return (
    <nav className={styles.appNav}>
      {NAV_ITEMS.map(({ key, label, Icon, primary }) => {
        const active = key === activeRoute;
        const badge = key === 'part-orders' && partsToOrderCount > 0
          ? partsToOrderCount
          : null;
        return (
          <button
            type="button"
            key={key}
            className={[
              styles.navItem,
              active ? styles.navItemActive : '',
              primary ? '' : styles.navItemFoldable,
            ].filter(Boolean).join(' ')}
            onClick={() => onNavChange(key)}
            aria-current={active ? 'page' : undefined}
          >
            <span className={styles.navIcon}>
              <Icon size={18} stroke={1.5} />
            </span>
            <span className={styles.navLabel}>{label}</span>
            {badge !== null && (
              <span
                className={styles.navBadge}
                aria-label={`${badge} parts to order`}
              >
                {badge}
              </span>
            )}
          </button>
        );
      })}

      <button
        type="button"
        ref={moreRef}
        className={[styles.navItem, styles.moreButton, moreActive ? styles.navItemActive : '']
          .filter(Boolean)
          .join(' ')}
        onClick={() => setMoreOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={moreOpen}
        aria-current={moreActive ? 'page' : undefined}
      >
        <span className={styles.navIcon}>
          <IconMenu2 size={18} stroke={1.5} />
        </span>
        <span className={styles.navLabel}>More</span>
        {partsToOrderCount > 0 && (
          <span className={styles.navBadge} aria-label={`${partsToOrderCount} parts to order`}>
            {partsToOrderCount}
          </span>
        )}
      </button>

      {moreOpen && (
        <ActionPicker
          options={moreOptions}
          currentValue={activeRoute}
          onSelect={(route) => onNavChange(route)}
          onClose={() => setMoreOpen(false)}
          anchorRef={moreRef}
        />
      )}
    </nav>
  );
};
